import { PresetType } from "../PresetSelection";

export interface IPlayerReportDataStreams {
    label: string;
    value: string;
    key: string;
    count: number;
    types: string[];
}

export interface IGeneralReportsConfig {
    report_type?: string,
    group_by_position?: boolean,
    date?: string,
    date_range?: any,
    emails?: any,
    player_report_data_streams?: string[],
    position_filter?: any,
    player_group_filter?: any,
    // The same values the tracker's flag filter uses: "gps" | "forceplate" | "testing" |
    // "udf:<id>". Empty means every flag.
    flag_filter?: string[],
    // How the Flag Overview report bands its rows. See FLAG_GROUP_BY_OPTIONS.
    group_by?: string,
    drill_filter?: any,
    dated_drill_filter?: any,
    player_filter?: any,
    comparison_type?: any,
    current_modal_type?: string,
    dual_date_ranges?: [string[], string[]],
    subjects_filter?: string[],
    aggregate_option?: 'avg' | 'best',
    aggregate_columns?: string[],
    weekly_streams?: string[],
    // ponytail: 49ers-only exception for percent_of_best_analysis briefing notes
    show_briefing_notes?: boolean,
    briefing_notes_metrics?: string[],
}

export const PLAYER_REPORT_DATA_STREAMS: IPlayerReportDataStreams[] = [
    {
        label: "One-Pager Report Card",
        value: "report-card",
        key: "report-card",
        count: 20,
        types: ["gps", "testing", "forceplate", "nutrition", "cognition"]
    },
    {
        label: "GPS",
        value: "gps",
        key: "gps",
        count: 4,
        types: ["gps"]
    },
    {
        label: "Forceplate",
        value: "forceplate",
        key: "forceplate",
        count: 4,
        types: ["forceplate"]
    },
    {
        label: "Testing",
        value: "testing",
        key: "testing",
        count: 4,
        types: ["testing"]
    },
]

export interface IReportTypes {
    label: string;
    key: string;
    requirements: string[]
    show_download?: boolean;
    metric_selector_info?: {
        count: number;
        types: string[];
        useAggs?: boolean;
        show_helper_text?: boolean;
        unique?: boolean;
        showAllMetrics?: boolean;
    };
    preset?: {
        key: string;
        presetType: PresetType
    }[];
    teams?: string[];
}

export const REPORT_TYPES: IReportTypes[] = [
    {
        label: 'GPS Report',
        key: 'gps',
        requirements: ['date', 'position_filter', 'player_group_filter', 'emails'],
        show_download: true,
    },
    {
        label: 'Gameday Comparison Report',
        key: 'game_day_comparison',
        requirements: ['date', 'position_filter', 'player_group_filter', 'emails']
    },
    {
        label: 'RPE Report',
        key: 'RPE',
        requirements: ['date', 'position_filter', 'player_group_filter', 'emails'],
        show_download: true,
    },
    {
        label: "Coach's Report (GPS)",
        key: 'coach-gps',
        requirements: ['date', 'position_filter', 'player_group_filter', 'emails'],
        show_download: true,
    },
    {
        label: "Percent of Best Analysis",
        key: 'percent_of_best_analysis',
        requirements: ['date_range', 'emails', 'metric_selector'],
        metric_selector_info: {
            count: 9,
            types: ['forceplate', 'testing'],
            useAggs: true,
            showAllMetrics: true,
        },
        teams: ['San Francisco 49ers'],
        show_download: true,
    },
    {
        label: 'Force Plate Report',
        key: 'forceplate',
        requirements: ['date', 'emails', 'group_by_position', 'position_filter', 'player_group_filter', 'comparison', 'metric_selector'],
        metric_selector_info: {
            count: 4,
            types: ['forceplate'],
            useAggs: true,
            unique: true
        },
        preset: [
            {
                key: 'forceplate',
                presetType: PresetType.ForcePlateReportMetrics
            }
        ]
    },
    {
        label: 'Testing Report',
        key: 'testing',
        requirements: ['date', 'emails', 'group_by_position', 'position_filter', 'player_group_filter', 'comparison', 'metric_selector'],
        metric_selector_info: {
            count: 4,
            types: ['testing'],
            useAggs: true,
            unique: true
        },
        preset: [
            {
                key: 'testing',
                presetType: PresetType.TestingReportMetrics
            }
        ]
    },
    {
        label: 'Player Report',
        key: 'player_report',
        requirements: ['single_player_filter', 'player_report_type_selector', 'date_range', 'emails'],
        show_download: true,
        metric_selector_info: {
            count: 0,
            types: [],
        },
        preset: [
            {
                key: 'report-card',
                presetType: PresetType.PlayerReportMetrics
            },
            {
                key: 'gps',
                presetType: PresetType.GpsReportMetrics
            },
            {
                key: 'forceplate',
                presetType: PresetType.ForcePlateReportMetrics
            },
            {
                key: 'testing',
                presetType: PresetType.TestingReportMetrics
            }
        ]
    },
    {
        label: "Team Report Cards - Bulk Export",
        key: 'report-card-bulk',
        requirements: ['date_range', 'position_filter', 'player_group_filter', 'emails', 'metric_selector'],
        metric_selector_info: {
            count: 20,
            types: ['gps', 'forceplate', 'testing', 'nutrition', "cognition"],
            useAggs: true,
        },
        preset: [
            {
                key: 'report-card-bulk',
                presetType: PresetType.TeamReportMetrics
            }
        ]
    },
    {
        label: 'Daily Report (All Tech)',
        key: 'daily_all_tech',
        requirements: ['date', 'emails']
    },
    {
        label: 'Weekly Report',
        key: 'weekly',
        requirements: ['date_range', 'emails', 'weekly_metric_selectors'],
        metric_selector_info: {
            count: 0,  // Not used for weekly reports
            types: [],
            useAggs: true,
            show_helper_text: false,
            unique: true,
        },
        preset: [
            {
                key: 'gps',
                presetType: PresetType.GpsReportMetrics
            },
            {
                key: 'testing',
                presetType: PresetType.TestingReportMetrics
            },
            {
                key: 'forceplate',
                presetType: PresetType.ForcePlateReportMetrics
            }
        ]
    },
    {
        label: 'Wellness Report',
        key: 'wellness',
        requirements: ['date', 'position_filter', 'player_group_filter', 'emails']
    },
    {
        label: 'Single Week Distance Breakdown',
        key: 'single_week_distance_breakdown',
        requirements: ['date_range', 'emails'],
        teams: ['Las Vegas Raiders']
    },
    {
        label: 'Multi Week Distance Breakdown',
        key: 'multi_week_distance_breakdown',
        requirements: ['date_range', 'emails'],
        teams: ['Las Vegas Raiders']
    },
    {
        label: 'One Pager Body Weight Report',
        key: 'body_weight_report',
        requirements: ['date_range', 'emails'],
        teams: ['Las Vegas Raiders']
    },
    {
        label: 'Weekly Body Weight Report',
        key: 'weekly_body_weight_report',
        requirements: ['date_range', 'emails'],
        teams: ['Las Vegas Raiders']
    },
    {
        label: 'Custom Breakdown Table',
        key: 'custom_breakdown_table',
        requirements: ['date_range', 'position_filter', 'player_group_filter', 'emails', 'metric_selector'],
        show_download: true,
        metric_selector_info: {
            count: 6,
            types: ['gps', 'forceplate', 'testing', 'nutrition', 'wellness', "cognition"],
            useAggs: true,
        },
        preset: [
            {
                key: "custom_breakdown_table",
                presetType: PresetType.CustomBreakdownReportMetrics
            }
        ]
    },
    {
        label: 'Status Report',
        key: 'status_report',
        requirements: ['date_range', 'emails', 'position_filter', 'player_group_filter'],
        show_download: true,
    },
    {
        label: 'ACWR One Page Report',
        key: 'acwr-one-pager',
        requirements: ['date', 'position_filter', 'player_group_filter', 'emails']
    },
    {
        label: 'Player Date Range Comparison Report',
        key: 'player_date_range_report',
        requirements: ['dual_date_ranges', 'subjects_filter', 'metric_selector', 'aggregate_selector', 'aggregate_columns_selector', 'emails'],
        metric_selector_info: {
            count: 15,
            types: ['testing', 'forceplate', 'nutrition', "cognition"],
            useAggs: false,
            unique: true
        }
    },
    {
        label: 'Top Speed Report',
        key: 'top-speed-table-report',
        requirements: ['date', 'position_filter', 'player_group_filter', 'emails'],
        show_download: true,
    },
    {
        label: 'Overview Table Report',
        key: 'overview-table-report',
        requirements: ['date', 'position_filter', 'player_group_filter', 'emails'],
        show_download: true,
    },
    {
        label: 'Shanahan Report',
        key: 'positional-snap-report',
        requirements: ['date', 'position_filter', 'player_group_filter', 'dated_drill_filter', 'emails'],
        show_download: true,
        teams: ['San Francisco 49ers'],
    },
    {
        label: 'Flag Overview Report',
        key: 'flag-one-pager',
        requirements: ['date_range', 'position_filter', 'player_group_filter', 'flag_filter', 'group_by', 'emails'],
        show_download: true,
    },
    {
        label: 'Positional Report',
        key: 'in-season-report',
        requirements: ['date', 'emails'],
        show_download: true,
        teams: ['San Francisco 49ers'],
    },
].sort((a, b) => a.label.localeCompare(b.label));

// How the Flag Overview report bands its rows. "position" bands by position and shows a
// flag column. "flag" bands by flag and shows a position column. The backend uses the
// same two values and falls back to the default.
export const FLAG_GROUP_BY_POSITION = 'position';
export const FLAG_GROUP_BY_OPTIONS = [
    { label: 'Position', value: FLAG_GROUP_BY_POSITION },
    { label: 'Flag', value: 'flag' },
];

export const COMPANY_LOGO = "https://gpsdv-marketing.s3.us-east-2.amazonaws.com/logo/gpsdv_logo.png"